import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { MessageService } from './message.service';

import { Exception } from '../models/exception';

/** Type of the handleError function returned by HttpErrorHandler.createHandleError */
export type HandleError = (operation?: string, error?: any, data?: any) => void;

/** Handles HttpClient errors */
@Injectable()
export class ErrorHandler {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, private _messageService: MessageService) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
  }

  /** Create curried handleError function that already knows the component name */
  // tslint:disable:semicolon max-line-length
  createHandleError = (componentName = '') => (operation = 'operation', error: any = null, data: any = null) =>
    this.handleError(componentName, operation, error, data);
  // tslint:enable:semicolon max-line-length

  /**
   * Returns a function that handles operation failures.
   * This error handler lets the app continue to run as if no error occurred.
   * @param componentName = name of the component that attempted the operation
   * @param operation - name of the operation that failed
   * @param error - error object
   */
  handleError(componentName = '', operation = 'operation', error, data) {
    const errorString = JSON.stringify(error);
    const dataString = JSON.stringify(data);

    if (this._logging.errorTraceLogging) {
      console.error(`${componentName}.${operation} :: ${errorString} :: ${dataString}`);
    }

    if (this._logging.saveErrorToDB) {
      const exception: Exception = {
        eventId: null,
        logDateTime: null,
        source: componentName,
        operation: operation,
        error: `${errorString}`,
        data: `${dataString}`
      };

      this._saveNewExceptionLog(exception).subscribe();
    }

    if (this._logging.saveErrorToConsoleLog) {
      console.error(error);
    }

    // TODO: better job of transforming error for user consumption
    this._messageService.add(`${componentName}: ${operation} failed: ${errorString} data: ${dataString}`);
  }

  private _saveNewExceptionLog(exception: Exception): Observable<any> {
    const serviceUrl: string = this._baseServiceUrl + 'Exception/Add';

    if (this._logging.serviceCallTraceLog) {
      console.log('ErrorHandler.handleError :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post(serviceUrl, exception).pipe(
      retry(this._apiSettings.retryCount),
      catchError(err => {
        console.error(err);
        return null;
      })
    );
  }
}
